import { Link } from "react-router-dom";
import { HardHat, Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { COMPANY, SERVICES } from "../../data/site";
import { scrollToSection } from "../../lib/scroll";

const QUICK_LINKS = [
  { label: "Home", href: "#home" },
  { label: "About Us", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Projects", href: "#projects" },
  { label: "Get a Quote", href: "#contact" },
];

const SOCIALS = [
  { icon: Facebook, label: "Facebook" },
  { icon: Instagram, label: "Instagram" },
  { icon: Linkedin, label: "LinkedIn" },
  { icon: Twitter, label: "Twitter" },
];

export const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-black/10 bg-coal">
    <div className="border-b border-black/5 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="font-display text-[clamp(3rem,9vw,8rem)] font-black uppercase leading-[0.9] tracking-tight">
          <span className="text-ink">Sunnys</span>{" "}
          <span className="text-outline-bronze">Construction</span>
        </div>
      </div>
    </div>

    <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 md:grid-cols-2 lg:grid-cols-4">
      <div>
        <img src="/logo.png" alt="Sunny's Construction Ltd" className="h-12 w-auto" />
        <p className="mt-6 text-sm leading-relaxed text-fog">
          Building contractors based in Hounslow, serving London and the UK.
          Registered in England &amp; Wales — 19 years of experience on every
          job.
        </p>
        <div className="mt-6 flex gap-3">
          {SOCIALS.map((social) => {
            const Icon = social.icon;
            return (
              <a
                key={social.label}
                data-testid={`social-${social.label.toLowerCase()}`}
                href="#home"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection("#home");
                }}
                aria-label={social.label}
                className="grid h-10 w-10 place-items-center border border-black/15 text-black/60 transition-colors duration-300 hover:border-bronze hover:text-bronze"
              >
                <Icon className="h-4 w-4" />
              </a>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-[0.3em] text-bronze">
          Quick Links
        </h4>
        <ul className="mt-6 space-y-3">
          {QUICK_LINKS.map((link) => (
            <li key={link.label}>
              <button
                data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => scrollToSection(link.href)}
                className="text-sm text-fog transition-colors duration-300 hover:text-ink"
              >
                {link.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-[0.3em] text-bronze">
          Services
        </h4>
        <ul className="mt-6 space-y-3">
          {SERVICES.map((service) => (
            <li key={service.n}>
              <button
                data-testid={`footer-service-${service.n}`}
                onClick={() => scrollToSection("#services")}
                className="text-left text-sm text-fog transition-colors duration-300 hover:text-ink"
              >
                {service.title}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-[0.3em] text-bronze">
          Contact
        </h4>
        <ul className="mt-6 space-y-4 text-sm text-fog">
          <li data-testid="footer-office">{COMPANY.office}</li>
          <li>
            <a
              data-testid="footer-phone"
              href={COMPANY.phoneHref}
              className="transition-colors duration-300 hover:text-ink"
            >
              {COMPANY.phone}
            </a>
          </li>
          <li>
            <a
              data-testid="footer-email"
              href={`mailto:${COMPANY.email}`}
              className="transition-colors duration-300 hover:text-ink"
            >
              {COMPANY.email}
            </a>
          </li>
          <li>{COMPANY.hours}</li>
        </ul>
      </div>
    </div>

    <div className="border-t border-black/10">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-6 py-6 text-[11px] uppercase tracking-[0.2em] text-black/40 md:flex-row md:items-center md:justify-between">
        <span data-testid="footer-copyright">
          © 2026 {COMPANY.name}. All rights reserved.
        </span>
        <div className="flex items-center gap-6">
          <Link
            data-testid="footer-cookies-link"
            to="/cookies"
            className="transition-colors duration-300 hover:text-bronze"
          >
            Cookies Policy
          </Link>
          <Link
            data-testid="footer-admin-link"
            to="/admin"
            className="transition-colors duration-300 hover:text-bronze"
          >
            Admin
          </Link>
          <span data-testid="footer-company-number">
            Company No. {COMPANY.number} · Registered Office: {COMPANY.office}
          </span>
        </div>
      </div>
    </div>
  </footer>
);
